// @flow
/** @jsx isolateComponent */

import { Colors, Link, Text } from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

type SectionLink = {
  image?: string,
  text: string,
  href: string,
};

export type Section = {
  title: string,
  links: Array<SectionLink>,
};

type Props = {|
  /**
   * Sections with links to display in the dropdown menu
   */
  sections: Array<Section>,
|};

const Styles = labelStyles(__filename, {
  root: {
    padding: 30,
    width: '100%',
  },
  content: {
    maxWidth: 1200, // wrap content on larger screens
    display: 'grid',
    gridGap: 20,
    gridTemplateColumns: '1fr 1fr 1fr',
    margin: '0 auto',
  },
  link: hasImage => [
    {
      alignItems: 'center',
      borderRadius: 10,
      color: Colors.grey700,
      display: 'grid',
      fontWeight: 600,
      gridAutoFlow: 'column',
      gridGap: 20,
      lineHeight: '30px',
      padding: '5px 15px',
      width: '100%',
      '&:hover, &:focus': {
        color: Colors.blue500,
        backgroundColor: Colors.blue100,
      },
    },
    hasImage &&
      withModifier('hasImage', {
        padding: 10,
        lineHeight: '20px',
      }),
  ],
  image: {
    borderRadius: 10,
    height: 60,
    width: 60,
  },
  list: length => [
    {
      display: 'grid',
      gridAutoFlow: 'column',
      gridTemplateRows: `repeat(${length < 6 ? length : 6}, min-content)`,
    },
    length > 6 && {
      whiteSpace: 'nowrap',
    },
  ],
});

const renderSection = section => (
  <section key={section.title.replace(' ', '-')}>
    <Text margin="10px 10px 20px 15px" tag="h4" type="subtitle800">
      {section.title}
    </Text>
    <ul css={Styles.list(section.links.length)}>
      {section.links.map(link => (
        <li key={link.href}>
          <Link color={null} css={Styles.link(!!link.image)} href={link.href}>
            {link.image && (
              <img
                alt={link.text}
                css={Styles.image}
                role="presentation"
                src={link.image}
              />
            )}
            {link.text}
          </Link>
        </li>
      ))}
    </ul>
  </section>
);

/**
 * Submenu component acts as a dropdown on universal nav links
 */
const Submenu = (props: Props) => {
  return (
    <div css={Styles.root}>
      <div css={Styles.content}>
        {props.sections.map(section => renderSection(section))}
      </div>
    </div>
  );
};

export default Submenu;
